import { useState } from 'react'
import { Sheet } from '../ui/Sheet'

/**
 * Shown instead of the `@` picker when the agent you are typing to has never read the
 * messaging guide.
 *
 *   owner, 2026-08-07: 使用者想讓新的 Agent-a 傳訊息, 但是 Agent-a 還沒讀過 readme
 *   […] 使用者在視窗裡按下 Agree 之後自動送出給 Agent 執行 avengents readme 的信息
 *   […] 使用者如果拒絕, 那麼在 Agent-a 的 Text input 就不會彈出任何的 Agent List.
 *
 * This is the courtesy half. The guarantee is on the wire: every message to an agent
 * that has not read the guide carries the instruction in its trailer, local or remote,
 * whether a person was involved or not. What this adds is stopping a user from composing
 * a mention that the agent could not act on — asking before the disappointment rather
 * than after it.
 *
 * It gates on the SENDER. `@` here composes something *this* agent will have to act on,
 * so this agent's readiness is the question. Whether the target has read anything is not
 * this dialog's business; gating on that would make one unread agent unmentionable from
 * every composer in the app.
 */
export function GuideGate({
  session,
  pending,
  error,
  onAgree,
  onDecline,
  arrival = false,
}: {
  session: string
  /**
   * Opened by ARRIVING at the session rather than by typing `@`.
   *
   *   owner, 2026-08-29: 新建 Agent 以及 Fork Agent 在使用者進入 Agent session 的畫面
   *   就直接彈出來 README 的提示 […] 或是 ignore.
   *
   * Same decision, different moment, so the same sheet — but the copy has to change
   * with the moment. The `@` version explains a picker that did not open; nobody
   * arriving at a session has asked for a picker, and telling them mentions are
   * blocked would explain a thing they were not doing. The dismissal changes too:
   * "Not now" reopens next time by design, while arrival needs an answer that STAYS
   * answered for this guide — otherwise the prompt meets them at every visit.
   */
  arrival?: boolean
  /** True while the ask is being delivered — the agent still has to run it. */
  pending: boolean
  /**
   * Why the ask did not go, if it did not.
   *
   * The first version had no error path at all, and the first thing that went wrong
   * was invisible: the endpoint had never been deployed, so the POST 404'd and Agree
   * did nothing at all — owner: 「按下 Agree 之後並沒有將訊息傳給 Agent」. A button
   * that succeeds and a button that fails must not look identical, least of all here,
   * where "nothing visibly happened" is also what SUCCESS looks like.
   */
  error?: string | null
  /**
   * `auto` is whether the person ticked "automatically approve next time" — owner,
   * 2026-09-21: 都需要 Approve 這個動作。有點麻煩…加上一個 Checkbox.
   *
   * Handed to the CALLER rather than written here, because this component decides
   * nothing that outlives itself: the caller owns both the preference and the ask,
   * and a sheet that quietly wrote a stored setting would be a second place that
   * knows about it.
   */
  onAgree: (auto: boolean) => void
  onDecline: () => void
}) {
  const [auto, setAuto] = useState(false)
  return (
    <Sheet
      title={arrival ? 'Let this agent read the messaging guide?' : 'This agent hasn’t read the messaging guide'}
      onClose={onDecline}
    >
      {/*
        KEEP THE COMPOSER FOCUSED. This dialog renders while an `@` is pending, and the
        composer textarea does `onBlur={() => setMention(null)}` — so a plain click here
        blurs it, clears the mention, and unmounts this dialog BEFORE the click finishes.
        The button then never fires and the whole thing just disappears.
        Owner: 「按下 Agree 之後並沒有將訊息傳給 Agent」 — twice, and this was the second
        cause.
        AgentMention:106 guards the same way for the same reason; every button in the
        composer row does too. One `onMouseDown` on the wrapper covers this whole sheet,
        including the ✕ and the scrim, so a control added later cannot miss it.
      */}
      <div className="gg-body" onMouseDown={(e) => e.preventDefault()}>
        {arrival ? (
          <p>
            <b>{session}</b> hasn’t read the current messaging guide. It needs to run{' '}
            <code>avengents readme</code> once — that’s where the commands and the rules
            live, and it’s what lets this agent message the others.
          </p>
        ) : (
          <p>
            <b>{session}</b> can’t message other agents yet. It needs to run{' '}
            <code>avengents readme</code> once — that’s where the commands and the rules
            live.
          </p>
        )}
        {/*
          Said plainly, because it is the part that surprises people: agreeing sends a
          request, it does not flip a switch. The agent may sit on its own approval
          prompt, and then someone has to go and answer it. Promising more than that
          here would make the mention list's continued absence look like a bug.
        */}
        <p className="gg-note">
          Agreeing sends {session} a message asking it to run that. It has to actually run
          it before mentions work — and if its CLI stops to ask permission, that prompt is
          waiting for you in its terminal.
          {arrival && ' Ignore keeps this agent quiet until the guide changes.'}
        </p>
        {error && <p className="err-line gg-err">Couldn’t ask {session}: {error}</p>}
        {/*
          ON THE AGREE SIDE OF THE DIALOG, and worded as what it does rather than as
          a setting: it repeats THIS button, it does not approve anything else. An
          agent's own CLI permission prompt still waits in its terminal, which the
          note above already says and this must not appear to change.

          It travels with the click instead of being applied on toggle, so ticking it
          and then choosing Ignore leaves nothing behind — the box is part of the
          answer, not a switch that fires while the question is still open.
        */}
        <label className="gg-auto">
          <input
            type="checkbox"
            checked={auto}
            onChange={(e) => setAuto(e.target.checked)}
          />
          <span>Automatically approve next time</span>
        </label>
        <div className="gg-acts">
          {/*
            "Ignore" is a decision that is REMEMBERED (for this guide); "Not now" is
            not. The words have to differ because the outcomes do — a person who reads
            "Not now" and gets permanent silence has been misled, and one who reads
            "Ignore" and is asked again on the next visit stops believing the button.
          */}
          <button type="button" className="gg-btn" onClick={onDecline}>
            {arrival ? 'Ignore' : 'Not now'}
          </button>
          <button
            type="button"
            className="gg-btn primary"
            disabled={pending}
            onClick={() => onAgree(auto)}
          >
            {pending ? 'Sending…' : 'Agree — ask it to read'}
          </button>
        </div>
      </div>
    </Sheet>
  )
}
